import { useTranslation } from 'react-i18next'
import type { ReactElement } from 'react'
import { WarningCircle, Prohibit } from '@phosphor-icons/react'
import type { SchemaStation, SchemaStationTimetable } from '../../schema/types'
import { useStationStatuses } from '../../state/stationStatusContext'
import { openTodayRange } from './openTodayRange'
import { resolveRouteCardClock } from '../../state/routeCardClock'
import { stationServiceState } from './stationServiceState'
import { accessibilityFacts } from './facilityCatalog'
import type { StationContent } from './fetchStationContent'
import { StationFactRow } from './StationFactRow'
import { StationMapPreview } from './StationMapPreview'
import { LineRoundelGlyph } from './StationPageGlyphs'
import { lineHexForTheme } from '../../schema/lineColor'
import { useStationPageTheme } from './useStationPageTheme'
import { useTitleOverflow } from './useTitleOverflow'
import factAddress from './assets/fact-address.svg'
import factOpenHours from './assets/fact-open-hours.svg'
import factServiceGood from './assets/fact-service-good.svg'
import factStepFree from './assets/fact-step-free.svg'
import styles from './StationOverview.module.css'

interface Props {
  station: SchemaStation
  /** URL locale segment for the interactive-map deep link. */
  locale: string
  /** Station-content record (address/description) — fetched by the page. */
  content: StationContent
  /**
   * Cluster-wide timetable (`clusterTimetable`) — "Open today" spans
   * every line serving the station. Falls back to the record's own.
   */
  timetable?: SchemaStationTimetable
}

/**
 * Overview section of the station-page template — Figma 8597:41917
 * (TASK-699, design TASK-584). Left: H1 with the line roundel, zone
 * subtitle, editorial description and the facts list (address, open
 * today, live service, step-free). Right: the read-only live map
 * preview whose click opens the main map with this station seeded as
 * the route builder's From.
 *
 * Address + description come from the station-content database seed
 * (`fetchStationContent`, decision 2026-08-04) — rows without data
 * simply don't render. Service is live `@metro/station-status`
 * (absent = Good service). Facts icons are the unified Figma exports
 * (8688:34607…34622, designer-provided 2026-08-04) — SF glyphs
 * vectorised at export, so no Apple-licensed font ships. Only the
 * unspecified disrupted Service states still fall back to Phosphor.
 */
export function StationOverview({ station, locale, content, timetable }: Props): ReactElement {
  const { t } = useTranslation('station-page')
  const theme = useStationPageTheme()
  const statuses = useStationStatuses()
  // Today's day class (weekday / saturday / sunday) decides the row; the
  // visual baselines freeze it through the same seam as the route card.
  const now = resolveRouteCardClock()?.() ?? new Date()
  const openToday = openTodayRange(timetable ?? station.stationTimetable, now)
  const service = stationServiceState(
    station.naptanId === undefined ? undefined : statuses[station.naptanId],
  )
  const stepFree = accessibilityFacts(station.facilities ?? []).stepFree
  // Compact H1 trigger: measured overflow (any locale, any glyphs), with
  // the >13-characters heuristic kept as the pre-measure initial guess so
  // obviously-long names never flash a clipped 76px frame.
  const { ref: titleRef, overflowing } = useTitleOverflow(station.localName)
  // Unified designer-exported icons; the disrupted Service states have
  // no exported variant yet — Phosphor stand-ins until design lands.
  const serviceIcon =
    service.tone === 'good' ? (
      <img src={factServiceGood} alt="" width={48} height={48} />
    ) : service.tone === 'advisory' ? (
      <WarningCircle size={40} weight="bold" color="var(--sp-status-advisory)" />
    ) : (
      <Prohibit size={40} weight="bold" color="var(--sp-status-closure)" />
    )

  return (
    <div className={styles.overview}>
      <div className={styles.leftColumn}>
        <div className={styles.heading}>
          {/* Repaints on a transfer: the resolved record's line colour —
              the same per-theme hex the map draws (pill == map). */}
          <span
            className={styles.roundel}
            data-testid="hero-roundel"
            style={{ color: lineHexForTheme(station.line, theme) }}
          >
            <LineRoundelGlyph size={55} />
          </span>
          <h1
            key={station.localName}
            ref={titleRef}
            className={
              station.localName.length > 13 || overflowing
                ? `${styles.title} ${styles.titleLong}`
                : styles.title
            }
          >
            {station.localName}
          </h1>
        </div>
        <p className={styles.subtitle}>
          {station.zone != null
            ? t('overview_subtitle_zone', { zone: station.zone })
            : t('overview_subtitle')}
        </p>
        {content.description !== undefined && (
          <p className={styles.description}>{content.description}</p>
        )}
        <div className={styles.facts}>
          {content.address !== undefined && (
            <StationFactRow
              icon={<img src={factAddress} alt="" width={48} height={48} />}
              label={t('fact_address_label')}
              value={content.address}
            />
          )}
          {openToday !== null && (
            <StationFactRow
              icon={<img src={factOpenHours} alt="" width={48} height={48} />}
              label={t('fact_open_today_label')}
              value={openToday}
            />
          )}
          <StationFactRow
            icon={serviceIcon}
            label={t('fact_service_label')}
            value={t(service.labelKey)}
            tone={service.tone}
          />
          <StationFactRow
            icon={<img src={factStepFree} alt="" width={48} height={48} />}
            label={t('fact_step_free_label')}
            value={stepFree ? t('fact_step_free_available') : t('fact_step_free_not_available')}
            tone={stepFree ? 'default' : 'muted'}
          />
        </div>
      </div>
      <div className={styles.mapColumn}>
        {/* /stations/<slug> seeds selectedStation exactly like a user
            tap (panel + focus + dim); ?from= additionally fills the
            route builder's origin. Verified live: the URL normalises to
            ?from=&line=&station= and both surfaces come up. */}
        <StationMapPreview
          stationX={station.x}
          stationY={station.y}
          href={`/${locale}/stations/${station.slug ?? ''}?from=${station.slug ?? ''}`}
        />
      </div>
    </div>
  )
}
